import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@auth/AuthProvider';
import PeopleController from '@data/controller/PeopleController';
import { Person, personFromResponse } from '@interfaces/person.interface';

// Resolves a person from an ID. A person already in hand is passed straight
// through so nothing is fetched.
export default function usePerson(personID?: string | null, providedPerson?: Person | null) {
  const auth = useAuth();
  const churchID = auth.churchId;

  const [person, setPerson] = useState<Person | null>(providedPerson ?? null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const shouldFetch = !providedPerson && Boolean(churchID) && Boolean(personID);

  const fetch = useCallback(async () => {
    if (!churchID || !personID) return;

    setLoading(true);
    setError(null);

    const response = await PeopleController.fetchPerson(churchID, personID, auth);
    const fetchedPerson = response.error ? null : personFromResponse(response.body);

    if (!fetchedPerson) {
      setError(response.error || 'Failed to load this person.');
      setPerson(null);
    } else {
      setPerson(fetchedPerson);
    }

    setLoading(false);
  }, [churchID, personID, auth]);

  useEffect(() => {
    if (providedPerson) {
      setPerson(providedPerson);
      setError(null);
      setLoading(false);
      return;
    }

    if (!shouldFetch) return;

    void fetch();
  }, [providedPerson, shouldFetch, fetch]);

  return { person, loading, error, refetch: fetch } as const;
}
